import Flex from "@/components/atoms/Flex";
import IconButton from "@/components/atoms/IconButton";
import Modal from "@/components/atoms/Modal";
import { SelectPaginateAsync } from "@/components/atoms/Select";
import { EMAIL_REGEX } from "@/constants/regexs";
import {
  INVALID_EMAIL2_MESSAGE,
  INVALID_FORM_MESSAGE,
  INVALID_MAXIMUM_CARACTERE_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
  INVALID_NAME_MESSAGE,
  INVALID_PHONE2_MESSAGE,
} from "@/constants/validatorMessages";
import { pluralize } from "@/helpers/utils";
import useLoadOptions from "@/hooks/useLoadOptions";
import { CompanyForm } from "@/interfaces/Company";
import { IModalMethods } from "@/interfaces/Modal";
import { useStoreCompanyMutation } from "@/services/companies";
import { faComment, faEllipsisH } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Col, Dropdown, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

const StoreCompanyModal: ForwardRefRenderFunction<IModalMethods> = (_, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const {
    handleSubmit,
    control,
    register,
    reset,
    formState: { errors },
  } = useForm<CompanyForm>();

  const { loadActivitySectors } = useLoadOptions();
  const [storeCompanyAsync] = useStoreCompanyMutation();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "notes",
  });

  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<CompanyForm> = async ({
    phone,
    fixPhone,
    owner,
    ...data
  }) => {
    const ownerFields = {
      ...(owner?.firstname && { firstname: owner.firstname }),
      ...(owner?.lastname && { lastname: owner.lastname }),
      ...(owner?.phone && {
        phone: parsePhoneNumber(owner.phone, "FR")!.number,
      }),
    };
    const ownerData =
      Object.keys(ownerFields).length > 0 ? ownerFields : undefined;

    await toast.promise(
      storeCompanyAsync({
        ...data,
        ...(ownerData && { owner: ownerData }),
        phone: phone && parsePhoneNumber(phone, "FR")!.number,
        fixPhone: fixPhone && parsePhoneNumber(fixPhone, "FR")!.number,
        activitySectorId: data.activitySector?.id || null,
      }).unwrap,
      {
        pending: "Ajout de l'entreprise en cours...",
        success: {
          render() {
            showOrHiddenModal();
            reset();
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                L&apos;entreprise à bien été ajoutée 🤩
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              const response = data?.data as any;
              if (response.errors) {
                const meetingError = response.errors.find(
                  (element: any) => element.field === "meetings"
                );
                const aeError = response.errors.find(
                  (element: any) => element.field === "AE"
                );
                const uniqueError = response.errors.find(
                  (element: any) => element.rule === "unique"
                );
                if (meetingError) {
                  return meetingError.args === "required"
                    ? "Vous devez ajouter au moins une date de rendez-vous."
                    : "Vous ne devez pas ajouter de rendez-vous.";
                } else if (aeError) {
                  return aeError.args === "required"
                    ? "Vous devez ajouter au moins un commercial"
                    : "Vous ne devez pas ajouter de commercial";
                } else if (uniqueError) {
                  return "L'entreprise existe déjà avec ce numérot de siret, ce numéro de téléphone et/ou bien cet email.";
                }
              }
              return "Les champs que vous avez remplis semblent être incorrects.";
            }

            return "Une erreur est survenue";
          },
        },
      }
    );
  };

  const onError: SubmitErrorHandler<CompanyForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <div>
      <Modal
        ref={ModalRef}
        title="Ajouter une entreprise"
        requiredLabel
        onSubmit={handleSubmit(onSubmit, onError)}
        beforeFooter={
          <Flex className="me-auto">
            <IconButton
              variant="primary"
              size="sm"
              icon={faComment}
              transform="shrink-3"
              iconAlign="middle"
              className="me-2"
              onClick={() => append({ message: "" })}
            >
              <span className="d-none d-lg-inline-block ms-1">Note</span>
            </IconButton>
          </Flex>
        }
      >
        <div>
          <Row className="gx-3">
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Nom de l'entreprise <span className="text-danger">*</span> :
                </Form.Label>
                <Form.Control
                  {...register("name", {
                    required: {
                      value: true,
                      message: INVALID_NAME_MESSAGE,
                    },
                    minLength: {
                      value: 2,
                      message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                    },
                    maxLength: {
                      value: 255,
                      message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                    },
                  })}
                  placeholder="ex : Crédit Agricole"
                  type="text"
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Siret <span className="text-danger">*</span> :
                </Form.Label>
                <Form.Control
                  placeholder="ex : 552 178 639 00132"
                  type="text"
                  {...register("siret", {
                    required: {
                      value: true,
                      message: "Le numéro de siret est obligatoire",
                    },
                    validate: {
                      isLength15: (value) =>
                        value.length === 14 ||
                        "Le numéro de siret doit comporter 14 chiffres",
                    },
                  })}
                  isInvalid={!!errors.siret}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.siret?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email :</Form.Label>
                <Form.Control
                  placeholder="ex : credit@agricole.com"
                  type="text"
                  {...register("email", {
                    pattern: {
                      value: EMAIL_REGEX,
                      message: INVALID_EMAIL2_MESSAGE,
                    },
                  })}
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Numéro de téléphone fixe :</Form.Label>
                <Form.Control
                  placeholder="ex : +33 1 34 83 55 32"
                  type="tel"
                  {...register("fixPhone", {
                    validate: (value) => {
                      if (value && !isValidPhoneNumber(value, "FR")) {
                        return INVALID_PHONE2_MESSAGE;
                      }
                    },
                  })}
                  isInvalid={!!errors.fixPhone}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.fixPhone?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Secteur d'activité</Form.Label>
                <Controller
                  control={control}
                  name="activitySector"
                  render={({ field: { value, onChange } }) => (
                    <SelectPaginateAsync
                      loadOptions={loadActivitySectors}
                      getOptionLabel={(option) =>
                        `${option.opco?.name} - ${option.name}`
                      }
                      getOptionValue={(option) => option.id.toString()}
                      isClearable
                      value={value}
                      onChange={onChange}
                      aria-invalid={!!errors.activitySector}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.activitySector?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Lien société.com</Form.Label>
                <Form.Control
                  placeholder="ex : https://"
                  type="text"
                  {...register("link")}
                  isInvalid={!!errors.link}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.link?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nombre d'établissement</Form.Label>
                <Form.Control
                  placeholder="ex : 2"
                  type="number"
                  {...register("defaultCompaniesNumber")}
                  isInvalid={!!errors.defaultCompaniesNumber}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.defaultCompaniesNumber?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nombre de salarié</Form.Label>
                <Form.Control
                  placeholder="ex : 4"
                  type="number"
                  {...register("defaultPeopleNumber")}
                  isInvalid={!!errors.defaultPeopleNumber}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.defaultPeopleNumber?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Adresse</Form.Label>
                <Form.Control
                  placeholder="ex : 3 Rue de la Paix, 75002 Paris"
                  type="string"
                  {...register("address")}
                  isInvalid={!!errors.address}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={3}>
              <Form.Group className="mb-3">
                <Form.Label>Ville :</Form.Label>
                <Form.Control
                  placeholder="ex : 3 Rue de la Paix, 75002 Paris"
                  type="string"
                  {...register("city")}
                  isInvalid={!!errors.city}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.city?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={3}>
              <Form.Group className="mb-3">
                <Form.Label>Code postale: </Form.Label>
                <Form.Control
                  placeholder="ex : 3 Rue de la Paix, 75002 Paris"
                  type="string"
                  {...register("zipCode")}
                  isInvalid={!!errors.zipCode}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.zipCode?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={12}>
              <Form.Group className="mb-3">
                <Form.Label>Formation(s) souhaitée(s) :</Form.Label>
                <Form.Control
                  {...register("defaultCourses")}
                  placeholder="ex : Suite Adobe, Pack Office"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.defaultCourses?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3 me-auto">
                <Form.Label>Est-ce un renouvellement ? :</Form.Label>
                <Form.Check
                  type="switch"
                  {...register("renewal")}
                  isInvalid={!!errors.renewal}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.renewal?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={12}>
              <h4 className="my-4">Interlocuteur :</h4>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Prenom :</Form.Label>
                <Form.Control
                  placeholder="ex : Marie"
                  type="text"
                  {...register("interlocutorFirstname")}
                />
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nom :</Form.Label>
                <Form.Control
                  placeholder="ex : Martin"
                  type="text"
                  {...register("interlocutorLastname")}
                />
              </Form.Group>
            </Col>
            <Col xs={12} className="mt-3">
              <hr />
              <h4 className="my-4">Informations du gérant :</h4>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Prenom :</Form.Label>
                <Form.Control
                  placeholder="ex : Remi"
                  type="text"
                  {...register("owner.firstname")}
                  isInvalid={!!errors.owner?.firstname}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.owner?.firstname?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nom :</Form.Label>
                <Form.Control
                  placeholder="ex : Dupont"
                  type="text"
                  {...register("owner.lastname")}
                  isInvalid={!!errors.owner?.lastname}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.owner?.lastname?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Numéro de portable :</Form.Label>
                <Form.Control
                  placeholder="ex : +33 6 34 83 55 32"
                  type="text"
                  {...register("owner.phone", {
                    validate: (value) => {
                      if (value && !isValidPhoneNumber(value, "FR")) {
                        return INVALID_PHONE2_MESSAGE;
                      }
                    },
                  })}
                  isInvalid={!!errors.owner?.phone}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.owner?.phone?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          {fields.length > 0 && (
            <>
              <hr className="my-3" />
              <Row className="gx-3">
                <h5 className="mb-3">{pluralize("Note", fields.length)} :</h5>
                {fields.map((_, index) => (
                  <div key={index}>
                    <Row className="flex-between-center align-items-center">
                      <Col xs={11}>
                        <Form.Group className="mb-3">
                          <Form.Label>Message</Form.Label>
                          <Form.Control
                            type="text"
                            {...register(`notes.${index}.message`, {
                              minLength: {
                                value: 3,
                                message:
                                  "Ce champ doit contenir au moins 3 caractères.",
                              },
                              maxLength: {
                                value: 255,
                                message:
                                  "Ce champ doit contenir au plus 255 caractères.",
                              },
                            })}
                            isInvalid={!!errors.notes?.[index]?.message}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={1} className="d-flex justify-content-end">
                        <Dropdown
                          align="end"
                          className="btn-reveal-trigger d-inline-block"
                        >
                          <Dropdown.Toggle
                            split
                            variant="nolina-default"
                            size="sm"
                          >
                            <FontAwesomeIcon
                              icon={faEllipsisH}
                              className="fs--2"
                            />
                          </Dropdown.Toggle>
                          <Dropdown.Menu className="border py-0">
                            <div className="py-2">
                              <Dropdown.Item
                                className="text-danger"
                                onClick={() => remove(index)}
                              >
                                Supprimer
                              </Dropdown.Item>
                            </div>
                          </Dropdown.Menu>
                        </Dropdown>
                      </Col>
                    </Row>
                    {index !== fields.length - 1 && <hr className="my-3" />}
                  </div>
                ))}
              </Row>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default forwardRef(StoreCompanyModal);
